class LocalizationDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawerClass = "wt-localization-drawer";
    this.drawer = this;
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "wt-localization-drawer-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    this.triggerQuery = [
      ".wt-localization-trigger",
      ".wt-localization-drawer__close",
      `.${this.pageOverlayClass}`,
    ].join(", ");

    this.isOpen = false;
    this.mainTrigger =
      document.querySelector(".wt-header__localization-trigger") || null;

    this.lastOpenerEl = null;

    this.selectors = {
      tabContent: ".wt-localization-drawer__tab__content",
      activeTabContent: ".wt-localization-drawer__tab__content.is-active",
      focusable:
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    };
  }

  connectedCallback() {
    this.init();
    this.inert = true;
    this.setAttribute("aria-hidden", "true");
  }

  // Save opener element (prefer clicked trigger, fallback to current focus)
  storeOpenerElement(triggerEl) {
    const candidate = triggerEl || document.activeElement;

    if (candidate && candidate instanceof HTMLElement) {
      this.lastOpenerEl = candidate;
    } else {
      this.lastOpenerEl = null;
    }
  }

  // Restore focus to opener (if still in DOM + focusable)
  restoreFocusToOpener() {
    const el = this.lastOpenerEl;

    if (!el) return false;
    if (!document.contains(el)) return false;

    // Avoid focusing disabled elements
    if (el.hasAttribute("disabled")) return false;

    // If element is hidden, focus may fail – still try safely
    try {
      el.focus();
      this.lastOpenerEl = null;
      return true;
    } catch {
      return false;
    }
  }

  // Elements that are ALWAYS allowed (close btn, tab triggers, etc.)
  // but NOT elements inside ANY tab content panels (so we can re-add only active tab panel later)
  getGlobalFocusableElements() {
    const all = Array.from(this.querySelectorAll(this.selectors.focusable));

    return all.filter((el) => {
      // Skip invisible elements
      if (el.offsetParent === null) return false;

      // Skip anything inside ANY tab content (we'll handle active tab separately)
      const isInsideAnyTabPanel = el.closest(this.selectors.tabContent);
      if (isInsideAnyTabPanel) return false;

      return true;
    });
  }

  // Focusables only inside active tab panel
  getActiveTabFocusableElements() {
    const activePanel = this.querySelector(this.selectors.activeTabContent);
    if (!activePanel) return [];

    return Array.from(
      activePanel.querySelectorAll(this.selectors.focusable),
    ).filter((el) => el.offsetParent !== null);
  }

  // Final "allowed" focusables for current state
  getAllowedFocusableElements() {
    return [
      ...this.getGlobalFocusableElements(),
      ...this.getActiveTabFocusableElements(),
    ];
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
    setTimeout(() => {
      document.body.classList.remove("no-focus-visible");
    }, 200);
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;

      this.inert = true;
      this.setAttribute("aria-hidden", "true");

      // Restore focus AFTER DOM/classes update
      requestAnimationFrame(() => {
        const restored = this.restoreFocusToOpener();
        if (!restored && this.mainTrigger) this.mainTrigger.focus();
      });
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;

      this.inert = false;
      this.setAttribute("aria-hidden", "false");

      const closeBtn = this.querySelector(".wt-localization-drawer__close");
      if (closeBtn) closeBtn.focus();
    }
    this.temporaryHideFocusVisible();
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  setActiveTab(tabKey) {
    const tabTriggers = this.querySelectorAll(
      ".wt-localization-drawer__tab__trigger",
    );
    const tabContents = this.querySelectorAll(
      ".wt-localization-drawer__tab__content",
    );

    tabTriggers.forEach((t) =>
      {
        t.classList.remove("wt-localization-drawer__tab__trigger--active");
        t.setAttribute('aria-selected', 'false');
      }
    );
    tabContents.forEach((c) => c.classList.remove("is-active"));

    const matchingTrigger = this.querySelector(`[data-tab-target="${tabKey}"]`);
    if (matchingTrigger) {
      matchingTrigger.classList.add(
        "wt-localization-drawer__tab__trigger--active",
      );
      matchingTrigger.setAttribute('aria-selected', 'true');
    }

    const matchingContent = this.querySelector(
      `[data-tab-content="${tabKey}"]`,
    );
    if (matchingContent) {
      matchingContent.classList.add("is-active");
    }
  }

  init() {
    // Trap focus & close on Escape
    this.addEventListener("keydown", (e) => {
      if (!this.isOpen) return;

      const isTab = e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const isEsc =
        e.key === "Escape" || e.keyCode === 27 || e.code === "Escape";

      if (isEsc) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }

      if (!isTab) return;

      // Recompute each time (handles tab switching and live DOM updates)
      const focusables = this.getAllowedFocusableElements();
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      // If focus somehow is outside allowed set, pull it to the start
      if (!focusables.includes(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    document.querySelectorAll(this.triggerQuery).forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();

        // IMPORTANT: remember what opened the drawer
        // Use currentTarget (the element with the listener), not e.target
        this.storeOpenerElement(e.currentTarget);

        const openTab = trigger.dataset.openDrawer;

        if (openTab) {
          this.setActiveTab(openTab);
        }

        this.toggleDrawerClasses();
      });
    });

    const searchInput = this.querySelector(
      ".wt-localization-drawer__search-country__input",
    );
    if (searchInput) {
      searchInput.addEventListener("input", () => {
        const searchValue = searchInput.value.toLowerCase().trim();

        this.querySelectorAll(".country-selector__item").forEach((item) => {
          const countryName = (item.dataset.filterName || "").toLowerCase();
          item.style.display = countryName.includes(searchValue) ? "" : "none";
        });
      });
    }

    const tabTriggers = this.querySelectorAll(
      ".wt-localization-drawer__tab__trigger",
    );
    const tabContents = this.querySelectorAll(
      ".wt-localization-drawer__tab__content",
    );

    tabTriggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        tabTriggers.forEach((t) => {
            t.classList.remove("wt-localization-drawer__tab__trigger--active");
            t.setAttribute('aria-selected', 'false');
        });
        tabContents.forEach((c) => c.classList.remove("is-active"));

        trigger.classList.add("wt-localization-drawer__tab__trigger--active");
        trigger.setAttribute('aria-selected', 'true');

        const targetKey = trigger.dataset.tabTarget;
        const matchedContent = this.querySelector(
          `[data-tab-content="${targetKey}"]`,
        );
        if (matchedContent) {
          matchedContent.classList.add("is-active");
        }
      });
    });

    const countryForm = document.querySelector("#DrawerCountryForm");
    if (countryForm) {
      const countryInput = countryForm.querySelector('[name="country_code"]');
      const countryLinks = this.querySelectorAll(".country-selector__trigger");

      countryLinks.forEach((link) => {
        link.addEventListener("click", (e) => {
          e.preventDefault();

          countryInput.value = link.dataset.value;
          countryForm.submit();
        });
      });
    }

    const languageForm = this.querySelector("form#DrawerLanguageForm");
    if (languageForm) {
      const radioButtons = languageForm.querySelectorAll(
        'input[name="language_code"]',
      );
      radioButtons.forEach((radio) => {
        radio.addEventListener("change", () => {
          languageForm.submit();
        });
      });
    }
  }
}

customElements.define("localization-drawer", LocalizationDrawer);
